import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

const RECYCLE_BIN_COLLECTION = "recycleBin";

export const RECYCLE_ENTITY_META = {
  account: {
    label: "Account",
    collectionKey: "accounts",
  },
  task: {
    label: "Task",
    collectionKey: "tasks",
  },
  inventory: {
    label: "Inventory Item",
    collectionKey: "inventory",
  },
};

export async function moveDocToRecycleBin({
  db,
  companyId,
  entityType,
  entityId,
  data,
  displayName,
  deletedByUid = null,
  deletedByRole = null,
  metadata = {},
}) {
  const entityMeta = RECYCLE_ENTITY_META[entityType];
  if (!db || !companyId || !entityMeta || !entityId || !data) {
    throw new Error("Missing recycle bin parameters.");
  }

  const recycleRef = collection(db, "companies", companyId, RECYCLE_BIN_COLLECTION);

  return addDoc(recycleRef, {
    entityType,
    entityLabel: entityMeta.label,
    collectionKey: entityMeta.collectionKey,
    entityId,
    displayName: displayName || entityId,
    data,
    metadata,
    status: "deleted",
    deletedAt: serverTimestamp(),
    deletedByUid,
    deletedByRole,
  });
}

export async function restoreRecycleBinEntry({
  db,
  companyId,
  recycleEntryId,
  restoredByUid = null,
  restoredByRole = null,
}) {
  if (!db || !companyId || !recycleEntryId) {
    throw new Error("Missing restore parameters.");
  }

  const recycleDocRef = doc(db, "companies", companyId, RECYCLE_BIN_COLLECTION, recycleEntryId);
  const recycleSnap = await getDoc(recycleDocRef);
  if (!recycleSnap.exists()) {
    throw new Error("Recycle bin entry not found.");
  }

  const entry = recycleSnap.data() || {};
  if (entry.status === "restored") {
    throw new Error("This item has already been restored.");
  }

  const collectionKey = entry.collectionKey;
  const entityId = entry.entityId;
  const payload = entry.data;

  if (!collectionKey || !entityId || !payload) {
    throw new Error("Recycle bin entry is missing restore data.");
  }

  const targetRef = doc(db, "companies", companyId, collectionKey, entityId);
  await setDoc(targetRef, {
    ...payload,
    restoredAt: serverTimestamp(),
    restoredBy: restoredByUid || null,
    updatedAt: serverTimestamp(),
  });

  await updateDoc(recycleDocRef, {
    status: "restored",
    restoredAt: serverTimestamp(),
    restoredByUid: restoredByUid || null,
    restoredByRole: restoredByRole || null,
  });
}

export async function permanentlyDeleteRecycleBinEntry({ db, companyId, recycleEntryId }) {
  if (!db || !companyId || !recycleEntryId) {
    throw new Error("Missing recycle bin delete parameters.");
  }
  await deleteDoc(doc(db, "companies", companyId, RECYCLE_BIN_COLLECTION, recycleEntryId));
}

export async function listRecycleBinEntries(db, companyId) {
  if (!db || !companyId) return [];
  const q = query(
    collection(db, "companies", companyId, RECYCLE_BIN_COLLECTION),
    orderBy("deletedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
